import { useQuery } from "@tanstack/react-query";
import request from "../../../request";

import { ClientOutput } from "@src/global_functions/backpackSdk/client";
import backpackSdk from "@src/global_functions/backpackSdk/sdk";
import { CamelCasedPropertiesDeep } from "type-fest";
import { VITE_TENANTS_AND_SPACES_BASE_URL } from "../../../../env";
import {
  bugsnagPostgrestErrorHandler,
  recursiveCamelCaseCipher,
} from "../../../postgrestApi/common";
import { FLOORS_QUERY_KEY, tenantsAndSpacesKeys } from "../queryKeyFactory";
import { Space, SpaceWithEnergyStarRequirements } from "../types";

export type BaseFloorWithSpaces = CamelCasedPropertiesDeep<
  Omit<ClientOutput["tenantsAndSpaces"]["FloorsWithSpaces"], "spaces">
>;

export type LegacyFloorWithSpaces = BaseFloorWithSpaces & {
  spaces: Space[];
};

export type FloorWithEStarSpaces = BaseFloorWithSpaces & {
  spaces: SpaceWithEnergyStarRequirements[];
};

export type FloorsApiType = {
  buildingId: number;
  floors: LegacyFloorWithSpaces[];
};

const getFloors = ({
  siteId,
  buildingId,
  includeEquipment,
}: {
  siteId: number;
  buildingId?: number | null;
  includeEquipment?: boolean;
}): Promise<FloorsApiType> => {
  if (typeof buildingId !== "number") {
    return Promise.reject(new Error("Invalid Building ID"));
  }
  return request
    .get(
      `${VITE_TENANTS_AND_SPACES_BASE_URL}/sites/${siteId}/buildings/${buildingId}/floors`
    )
    .query({ ...(includeEquipment ? { include: "Equipment" } : {}) })
    .then(({ body }) => {
      return recursiveCamelCaseCipher(body);
    })
    .catch((e) => bugsnagPostgrestErrorHandler(e));
};

export function useGetFloors(
  siteId: number,
  { includeEquipment = false }: { includeEquipment?: boolean } = {
    includeEquipment: false,
  }
) {
  const { data: buildingResponse } =
    backpackSdk.tenantsAndSpaces.building.useQuery(siteId);
  return useQuery({
    queryKey: [
      ...tenantsAndSpacesKeys.query(siteId, "floor", FLOORS_QUERY_KEY),
      buildingResponse?.id,
      includeEquipment,
    ],
    queryFn: () =>
      getFloors({
        siteId,
        buildingId: buildingResponse?.id,
        includeEquipment,
      }),
    enabled: !!buildingResponse?.id,
  });
}
